<template>
  <div class="report-viewer">
    <!-- V2 Mode: Iframe rendering of response_doc -->
    <div v-if="response_doc" class="report-viewer__v2">
      <!-- Control Bar for V2 -->
      <div class="container-fluid py-3">
        <div class="card shadow rounded-4 border-0">
          <div
            class="card-body py-3 px-4 d-flex justify-content-between align-items-center"
          >
            <div class="d-flex align-items-center gap-2">
              <span class="text-dark text-xs fw-bold">{{ title }}</span>
              <span class="badge bg-success text-white text-xxs"
                >PDF Document</span
              >
            </div>
            <div class="d-flex align-items-center gap-2">
              <!-- Export slot for custom export buttons -->
              <slot name="export-actions"></slot>

              <!-- Export Data button — opens the export modal -->
              <button
                v-if="service_type"
                @click="open_export_modal"
                class="btn btn-bg-green text-xs rounded-small shadow-sm"
              >
                <i class="bi bi-download me-1"></i>
                Export Data
              </button>

              <!-- Open PDF in new tab -->
              <a
                :href="response_doc"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-bg-green text-xs rounded-small shadow-sm"
              >
                <i class="bi bi-box-arrow-up-right me-1"></i>
                Open PDF
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Iframe -->
      <div class="report-viewer__iframe-container">
        <div
          class="ratio"
          :style="{ '--bs-aspect-ratio': iframe_aspect_ratio }"
        >
          <iframe
            :src="response_doc"
            class="report-viewer__iframe border rounded-3 shadow-sm"
            frameborder="0"
            allowfullscreen
            :title="title"
          ></iframe>
        </div>
      </div>

      <!-- Export Data Modal (V2 Mode) -->
      <ExportDataModal
        v-if="service_type"
        ref="export_modal"
        :modal_id="export_modal_id"
        :service_type="service_type"
        :data="export_data"
        :metadata="export_metadata"
      />
    </div>

    <!-- Legacy Mode: Render the report component normally -->
    <div v-else class="report-viewer__legacy">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { ExportDataModal } from "./export_modals";

export default {
  name: "ReportViewer",
  components: {
    ExportDataModal,
  },
  props: {
    /**
     * URL to the pre-generated PDF document.
     * When provided, the report is rendered in an iframe.
     * When null/undefined/empty, the default slot (legacy report component) is rendered.
     */
    response_doc: {
      type: String,
      default: null,
      required: false,
    },
    /**
     * Title displayed in the control bar.
     */
    title: {
      type: String,
      default: "Report",
      required: false,
    },
    /**
     * Aspect ratio for the iframe container (CSS value for --bs-aspect-ratio).
     * Defaults to 141% which approximates A4 portrait.
     */
    iframe_aspect_ratio: {
      type: String,
      default: "141%",
      required: false,
    },
    /**
     * Service type for data export (e.g., 'aml', 'ocr_liveness')
     * Required for export functionality
     */
    service_type: {
      type: String,
      default: null,
      required: false,
      validator: (value) =>
        !value ||
        [
          "aml",
          "ocr_liveness",
          "individual_liveness",
          "comprehensive_liveness",
          "rsa_id_verification",
          "ocr_verification",
          "id_liveness",
          "enterprise_aml",
          "enterprise_credit",
          "huru_criminal_check",
          "credit_report_namibia"
        ].includes(value),
    },
    /**
     * Data to export (report data structure)
     */
    export_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    /**
     * Additional metadata for export (sheet_elements, response_info, etc.)
     */
    export_metadata: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  computed: {
    /**
     * Whether the viewer is in v2 (iframe) mode.
     */
    is_v2() {
      return !!this.response_doc;
    },
    /**
     * Unique modal ID for this instance
     */
    export_modal_id() {
      return `export-modal-${
        this._uid || Math.random().toString(36).substr(2, 9)
      }`;
    },
  },
  methods: {
    open_export_modal() {
      if (this.$refs.export_modal) {
        this.$refs.export_modal.open_modal();
      }
    },
  },
};
</script>

<style scoped>
.report-viewer__iframe-container {
  padding: 0 1rem;
}

.report-viewer__iframe {
  width: 100%;
  height: 100%;
  min-height: 600px;
  background: #f8f9fa;
}

.report-viewer__legacy {
  /* Slot content handles its own layout */
  display: contents;
}

.badge {
  font-size: 0.65rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}
</style>
